import React, {
  useState, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { NavLink } from 'react-router-dom';
import {
  Container, Row, Col, Form
} from 'react-bootstrap';

import BaseView from '../BaseView';
import NewProblemModal from './NewProblemModal';
import ProblemListItem from './ProblemListItem';

import { getProblems } from '../../common/redux/slices/ProblemAPISlice';

export default function ProblemsView(props, context) {
  const dispatch = useDispatch();

  const profile = useSelector((state) => state.ProfileState.data);
  const { list, loading, error } = useSelector((state) => state.ProblemSlicedState);

  const [showResolved, setShowResolved] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Ref for the observer
  const observerRef = useRef(null);

  // Initial load
  useEffect(() => {
    loadInitialProblems();
  }, [showResolved]);

  // Set up the intersection observer
  useEffect(() => {
    // Only set up the observer after the initial load is complete
    if (initialLoad || !hasMore || loading) return;

    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    };

    const handleObserver = (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
        loadMoreProblems();
      }
    };

    const observer = new IntersectionObserver(handleObserver, options);
    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, loading, initialLoad, list.length]);

  // Helper function to extract cursor from URL
  const extractCursorFromUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('cursor');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return null;
    }
  };

  // Load initial problems
  const loadInitialProblems = () => {
    setInitialLoad(true);
    setNextCursor(null);
    setHasMore(true);

    dispatch(getProblems({
      show_resolved: showResolved,
      ordering: '-created_at'
    }))
      .unwrap()
      .then((data) => {
        setNextCursor(extractCursorFromUrl(data.next));
        setHasMore(!!data.next);
        setInitialLoad(false);
      })
      .catch((err) => {
        console.error('Error loading problems:', err);
        setHasMore(false);
        setInitialLoad(false);
      });
  };

  // Load more function for infinite scroll
  const loadMoreProblems = () => {
    if (!nextCursor || !hasMore || loading) return;

    console.log('Loading more problems with cursor:', nextCursor);

    dispatch(getProblems({
      show_resolved: showResolved,
      ordering: '-created_at',
      cursor: nextCursor
    }))
      .unwrap()
      .then((data) => {
        setNextCursor(extractCursorFromUrl(data.next));
        setHasMore(!!data.next);
        console.log('Loaded more problems, next cursor:', extractCursorFromUrl(data.next));
      })
      .catch((err) => {
        console.error('Error loading more problems:', err);
        setHasMore(false);
      });
  };

  const toggleShowResolved = () => {
    setShowResolved(!showResolved);
  };

  const handleNewProblems = () => {
    loadInitialProblems();
  };

  return (
    <BaseView title={context.t('Share a problem')} backurl="/">
      <hr className="afterTopNavigationBar"></hr>
      <Container style={{ marginTop: '-6px' }}>
        {profile.id ? (
          <div>
            <Row>
              <Col xs={6} style={{ textAlign: 'left' }}>
                <Form.Check
                  type="checkbox"
                  id="show-resolved-checkbox"
                  onChange={toggleShowResolved}
                  checked={showResolved}
                  style={{ display: 'inline', color: '#cccccc' }}
                  label={
                    <span
                      style={{
                        marginLeft: 5,
                        fontSize: '0.8rem',
                        verticalAlign: 'text-bottom',
                        color: '#6c757d'
                      }}
                    >
                      {context.t('Show resolved')}
                    </span>
                  }
                />
              </Col>
              <Col xs={6} style={{ textAlign: 'right' }}>
                {profile.type === 'JS' && (
                  <NewProblemModal handleNewProblems={handleNewProblems} />
                )}
              </Col>
            </Row>

            {/* Problem list */}
            {list.map((problem) => (
              <ProblemListItem key={`problems-${problem.id}`} problem={problem} />
            ))}

            {/* Sentinel element for intersection observer */}
            {hasMore && !loading && (
              <div
                ref={observerRef}
                style={{ height: '20px', margin: '10px 0' }}
              />
            )}

            {/* Loading indicator */}
            {loading && (
              <div className="text-center my-3">
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {context.t('Loading...')}
              </div>
            )}

            {/* End of list message */}
            {!hasMore && list.length > 0 && (
              <div className="text-center my-3 text-muted">
                {context.t('No more problems to load')}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="text-center my-3 text-danger">
                {context.t('Error loading problems. Please try again.')}
              </div>
            )}
          </div>
        ) : (
          <div>
            <NavLink to="/login">
              {context.t('Please login to share a problem..')}
            </NavLink>
          </div>
        )}
        {profile.type === 'JS' && list.length === 0 && !loading && !error && (
          <div className="centeredBox">
            <p style={{ color: '#6c757d' }}>
              {context.t(
                "You haven't shared any problems yet. Please click share a problem on the right top corner to share one."
              )}
            </p>
          </div>
        )}
      </Container>
    </BaseView>
  );
}

ProblemsView.contextTypes = {
  t: PropTypes.func.isRequired
};
